import { useState, type ReactNode } from 'react';

import { Modal } from '../../lib/Modal';
import { Button, Card, Icon } from '../../lib/ui';
import { getLang, t } from '../../lib/i18n';
import { GROUPS, type GroupId } from './types';
import { TOPICS, isTopicSlug, type TopicSlug } from './topics';

/**
 * Help where the question is asked, not in a help centre.
 *
 * Two rules decide everything here. First: the marker opens the topic **in place**, in a
 * dialog, rather than navigating to it. The banner editor holds an unsaved draft with an
 * undo history, and sending someone to a documentation page to find out what "equal
 * weight" means would cost them the work they were in the middle of.
 *
 * Second: a marker is only placed where the screen itself cannot answer the question. The
 * console already explains a great deal in `Field hint`, in empty states and on badges; a
 * question mark next to every label turns a designed screen into a form with a rash.
 */

/** The dialog, shared by both entry points. */
function useTopicDialog(): [ReactNode, (slug: TopicSlug) => void] {
  const [slug, setSlug] = useState<TopicSlug | null>(null);
  const lang = getLang();
  const topic = slug ? TOPICS[slug] : null;

  const node = (
    <Modal
      open={topic !== null}
      wide
      title={topic ? topic.title[lang] : ''}
      onClose={() => setSlug(null)}
      footer={
        <>
          {/* The way to the full page, for the reader who wants the neighbouring topics
              too. It closes the dialog first: leaving it open behind a route change means
              coming back to a modal nobody opened. */}
          <a
            href={`#/hilfe/${slug ?? ''}`}
            onClick={() => setSlug(null)}
            className="mr-auto self-center text-xs text-[var(--color-brand)] hover:underline"
          >
            {t('Alle Hilfethemen')}
          </a>
          <Button onClick={() => setSlug(null)}>{t('Schließen')}</Button>
        </>
      }
    >
      {topic && <div className="space-y-3">{topic.body[lang]}</div>}
    </Modal>
  );

  return [node, setSlug];
}

/**
 * The marker: a question mark that belongs to the thing beside it.
 *
 * Sized and coloured to recede — it sits next to headings and labels, and a help affordance
 * that competes with the control it explains is a worse screen than no help at all.
 */
export function Hilfe({ to }: { to: TopicSlug }) {
  const [dialog, open] = useTopicDialog();
  const label = t('Hilfe: {topic}', { topic: TOPICS[to].title[getLang()] });
  return (
    <>
      {dialog}
      <button
        type="button"
        onClick={() => open(to)}
        title={label}
        aria-label={label}
        className="ml-1 inline-grid h-[18px] w-[18px] shrink-0 place-items-center rounded-full
                   align-middle text-[var(--color-muted)] transition-colors
                   hover:bg-[var(--color-sunken)] hover:text-[var(--color-ink)]"
      >
        <Icon name="help" size={13} />
      </button>
    </>
  );
}

/**
 * The same thing as a phrase, for running text.
 *
 * A banner explaining a failure has room for "why is this blocked?" and no room for a
 * floating glyph.
 */
export function HilfeLink({ to, children }: { to: TopicSlug; children: ReactNode }) {
  const [dialog, open] = useTopicDialog();
  return (
    <>
      {dialog}
      <button
        type="button"
        onClick={() => open(to)}
        className="text-[var(--color-brand)] underline underline-offset-2"
      >
        {children}
      </button>
    </>
  );
}

/** The index, and the single-topic page behind a deep link. */
export function HelpPage({ slug }: { slug?: string }) {
  const lang = getLang();

  if (slug && isTopicSlug(slug)) {
    const topic = TOPICS[slug];
    return (
      <div className="space-y-4">
        <a href="#/hilfe" className="text-xs text-[var(--color-muted)] hover:underline">
          ← {t('Alle Hilfethemen')}
        </a>
        <h1 className="text-xl font-semibold">{topic.title[lang]}</h1>
        {/* Narrower than the console. Documentation set to the full 1560px is documentation
            nobody finishes a paragraph of. */}
        <div className="max-w-[62ch] space-y-3">{topic.body[lang]}</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold">{t('Hilfe')}</h1>
        <p className="mt-1 max-w-[62ch] text-[13px] text-[var(--color-muted)]">
          {t('Jedes Thema steht auch dort, wo es gebraucht wird — hinter dem Fragezeichen neben der jeweiligen Überschrift.')}
        </p>
      </div>

      {GROUPS.map((g) => (
        <Card key={g.id} title={g.title[lang]}>
          <ul className="divide-y divide-[var(--color-line)]">
            {slugsIn(g.id).map((s) => (
              <li key={s}>
                <a href={`#/hilfe/${s}`}
                  className="flex flex-col gap-0.5 py-2.5 transition-colors hover:bg-[var(--color-sunken)]">
                  <span className="text-[13px] font-medium">{TOPICS[s].title[lang]}</span>
                  <span className="text-xs text-[var(--color-muted)]">{TOPICS[s].teaser[lang]}</span>
                </a>
              </li>
            ))}
          </ul>
        </Card>
      ))}
    </div>
  );
}

const slugsIn = (g: GroupId): TopicSlug[] =>
  (Object.keys(TOPICS) as TopicSlug[]).filter((s) => TOPICS[s].group === g);
